import { ReactNode, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// ─────────────────────────────────────────────────────────────────────────────
// Floating — the ONE popover primitive for the whole app.
//
// Every custom dropdown/popover (SearchableSelect, TimeDropdown, PhoneInput,
// filter menus, comboboxes…) must render its panel through this. It portals the
// panel to <body> with position:fixed computed from the trigger rect, so it can
// NEVER be clipped by an `overflow-hidden` / `overflow-y-auto` ancestor (cards,
// drawers, scroll areas). It also:
//   - opens upward when there isn't room below,
//   - clamps to the viewport horizontally,
//   - closes on outside-click (accounting for the portalled panel) and Escape,
//   - re-positions on scroll/resize while open.
//
// Do NOT hand-roll `absolute z-50 mt-1` dropdowns anymore — use this.
// ─────────────────────────────────────────────────────────────────────────────

interface Pos {
  left: number;
  width?: number;
  top?: number;
  bottom?: number;
}

interface FloatingProps {
  open: boolean;
  /** Ref on the trigger (or its wrapper). The panel is positioned against it. */
  anchorRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  children: ReactNode;
  /** Match the panel width to the anchor's width. Default true. */
  matchWidth?: boolean;
  /** Gap in px between anchor and panel. Default 4. */
  gap?: number;
  /** Classes for the floating panel wrapper. */
  className?: string;
  /** Inline styles merged onto the panel wrapper. */
  style?: React.CSSProperties;
}

export function Floating({
  open,
  anchorRef,
  onClose,
  children,
  matchWidth = true,
  gap = 4,
  className = '',
  style,
}: FloatingProps) {
  const [pos, setPos] = useState<Pos | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const compute = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const panelH = panelRef.current?.offsetHeight ?? 280;
    const openUp = spaceBelow < panelH + gap && r.top > spaceBelow;

    // Keep the panel inside the viewport horizontally.
    const width = matchWidth ? r.width : panelRef.current?.offsetWidth ?? 0;
    let left = r.left;
    if (width) {
      const maxLeft = window.innerWidth - width - 8;
      if (left > maxLeft) left = Math.max(8, maxLeft);
    }

    setPos({
      left,
      ...(matchWidth ? { width: r.width } : {}),
      ...(openUp ? { bottom: window.innerHeight - r.top + gap } : { top: r.bottom + gap }),
    });
  }, [anchorRef, matchWidth, gap]);

  // Position while open; keep glued on scroll/resize. The rAF re-measures once
  // the panel has real dimensions (needed for openUp + horizontal clamp).
  useEffect(() => {
    if (!open) { setPos(null); return; }
    compute();
    const raf = requestAnimationFrame(compute);
    const onMove = () => compute();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, compute]);

  // Outside-click (both trigger and portalled panel are "inside") + Escape.
  useEffect(() => {
    if (!open) return;
    const onMouse = (e: MouseEvent) => {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onMouse);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouse);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, anchorRef, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      ref={panelRef}
      className={`fixed z-[9999] ${className}`}
      style={{
        left: pos?.left ?? -9999,
        ...(pos?.width != null ? { width: pos.width } : {}),
        ...(pos?.top != null ? { top: pos.top } : {}),
        ...(pos?.bottom != null ? { bottom: pos.bottom } : {}),
        // Hidden until measured so it never flashes at the wrong spot.
        visibility: pos ? 'visible' : 'hidden',
        ...style,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
